import { Activity, memo, useMemo, type PropsWithChildren } from "react"
import { useLocation } from "react-router-dom"

import { useTranslation } from "@be-miniapp/i18n"
import { ActionBar } from "@be-miniapp/ui/components/ui/action-bar"
import { cn } from "@be-miniapp/ui/lib/utils"

import { ActivityTab } from "./activity"
import { MainTab2 } from "./main/main-v2"
import { UserTab } from "./user"

/**
 * Shared viewport slot for home tabs. Keeps hidden Activity trees inside the same
 * bounds so tab headers/shadows do not bleed onto the visible tab.
 */
const HomeTabPane = memo(
  ({ children, active }: PropsWithChildren<{ active: boolean }>) => (
    <div
      className={cn(
        "absolute inset-0 bottom-14 isolate overflow-hidden",
        active ? "z-10" : "z-0"
      )}
    >
      {children}
    </div>
  )
)

HomeTabPane.displayName = "HomeTabPane"

const KNOWN_TAB_PATHS = new Set(["/", "/history", "/profile"])

const UnsupportedTabPage = memo(
  ({
    title,
    subtitle,
    description,
  }: {
    title: string
    subtitle: string
    description: string
  }) => (
    <div className="flex h-screen w-screen flex-col">
      <ActionBar border>{title}</ActionBar>
      <div className="flex w-full flex-1 flex-col items-center justify-center gap-2 px-5 pb-20">
        <span className="font-bold text-lg">{subtitle}</span>
        <span className="text-center text-sm">{description}</span>
      </div>
    </div>
  )
)

UnsupportedTabPage.displayName = "UnsupportedTabPage"

const Tabs = () => {
  const { t } = useTranslation()
  const { pathname } = useLocation()

  const tabPath = useMemo(
    () => pathname.replace("/ride/home", "") || "/",
    [pathname]
  )

  const isUnknownTab = !KNOWN_TAB_PATHS.has(tabPath)

  const isMainTab = tabPath === "/"
  const isActivityTab = tabPath === "/history"
  const isProfileTab = tabPath === "/profile"

  return (
    <div className="relative size-full min-h-0">
      <Activity mode={isMainTab ? "visible" : "hidden"}>
        <HomeTabPane active={isMainTab}>
          <MainTab2 />
        </HomeTabPane>
      </Activity>
      <Activity mode={isActivityTab ? "visible" : "hidden"}>
        <HomeTabPane active={isActivityTab}>
          <ActivityTab />
        </HomeTabPane>
      </Activity>
      <Activity mode={isProfileTab ? "visible" : "hidden"}>
        <HomeTabPane active={isProfileTab}>
          <UserTab />
        </HomeTabPane>
      </Activity>

      {isUnknownTab && (
        <UnsupportedTabPage
          title={t("home.rewards.title")}
          subtitle={t("home.rewards.subtitle")}
          description={t("common.non_supported.description")}
        />
      )}
    </div>
  )
}

export { Tabs }
